'use client'

import { Api } from '@/core/api'
import { env } from '@/lib/env'
import { ensureFreshKeycloakToken, refreshKeycloakToken } from '@/lib/auth-token'
import { AxiosRequestConfig, isAxiosError, RawAxiosRequestHeaders } from 'axios'

let api: Api

interface AuthRefreshAxiosRequestConfig extends AxiosRequestConfig {
  _retryAuthRefresh?: boolean
}

function setAuthorizationHeader(config: AxiosRequestConfig, token: string): void {
  config.headers = {
    ...((config.headers ?? {}) as RawAxiosRequestHeaders),
    Authorization: `Bearer ${token}`,
  }
}

function createApi() {
  if (!api) {
    // Use our centralized environment configuration
    const apiGatewayUrl = env.NEXT_PUBLIC_API_GATEWAY_URL
    const keycloakRealm = env.NEXT_PUBLIC_KEYCLOAK_REALM

    if (!apiGatewayUrl) {
      throw new Error('env variable missing: NEXT_PUBLIC_API_GATEWAY_URL')
    }
    if (!keycloakRealm) {
      throw new Error('env variable missing: NEXT_PUBLIC_KEYCLOAK_REALM')
    }

    api = new Api({ baseUrl: apiGatewayUrl })
    api.axios.defaults.headers.common['x-realm'] = keycloakRealm
    api.axios.interceptors.request.use(async (config) => {
      const token = await ensureFreshKeycloakToken()
      if (token) {
        setAuthorizationHeader(config, token)
      }

      return config
    })
    api.axios.interceptors.response.use(undefined, async (error: unknown) => {
      if (!isAxiosError(error) || error.response?.status !== 401 || !error.config) {
        return Promise.reject(error)
      }

      const config = error.config as AuthRefreshAxiosRequestConfig
      if (config._retryAuthRefresh) {
        return Promise.reject(error)
      }

      config._retryAuthRefresh = true

      try {
        const token = await refreshKeycloakToken()
        if (!token) {
          return Promise.reject(error)
        }

        setAuthorizationHeader(config, token)
        return api.axios(config)
      } catch {
        return Promise.reject(error)
      }
    })
  }
  return api
}

export default createApi()
